import React from "react"
import Link from "next/link"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import colors from "@constants/colors"
import { bodyBold, h6 } from "@constants/typography"
import { StyledLink } from "@components/buttons/LinkButton"
import HlsVideo from "@components/HlsVideo"
import breakpoints from "@constants/breakpoints"

interface VideoTileProps {
  thumbnailSrc?: string
  previewSrc?: string
  streamingUrl?: string
  fallbackUrl?: string
  link?: string
  openInNewTab?: boolean
  playOnce?: boolean
}

interface FeaturedVideoProps {
  title?: string
  sectionButtonText?: string
  sectionButtonLink?: string
  sectionButtonTextColor?: string
  sectionButtonColor?: string
  backgroundImageSrc?: string
  backgroundColor?: string
  primaryVideoThumbnail?: string
  primaryVideoPreview?: string
  primaryVideoStreamingUrl?: string
  primaryVideoFallbackUrl?: string
  primaryVideoLink?: string
  leftVideoThumbnail?: string
  leftVideoPreview?: string
  leftVideoStreamingUrl?: string
  leftVideoFallbackUrl?: string
  leftVideoLink?: string
  rightVideoThumbnail?: string
  rightVideoPreview?: string
  rightVideoStreamingUrl?: string
  rightVideoFallbackUrl?: string
  rightVideoLink?: string
}

const Section = styled.section<{ bgImage?: string; bgColor?: string }>`
  ${({ bgImage, bgColor }) => css`
    width: 100%;
    background-color: ${bgColor ? `#${bgColor}` : "#3c3d40"};
    background-image: ${bgImage
      ? `linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(${bgImage})`
      : "none"};
    background-size: cover;
    background-position: center;
  `}
`

const Container = styled.div`
  max-width: 1220px;
  margin: 0 auto;
  padding: 18px 12px 16px;

  ${up("laptop")} {
    padding: 16px 18px 20px;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
`

const Title = styled.h2`
  ${({ color }) => css`
    ${h6}
    margin: 0;
    color: ${color || colors.snow};
    font-size: 28px;
    line-height: 34px;

    ${up("laptop")} {
      font-size: 32px;
      line-height: 38px;
    }
  `}
`

const SectionButton = styled(StyledLink)`
  ${css`
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-size: 12px;
    height: 36px;
    padding: 0 15px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.22);
    gap: 12px;
    transition: opacity 160ms ease;

    &:hover,
    &:focus-visible {
      opacity: 0.7;
    }
  `}
`

const Grid = styled.div`
  display: grid;
  gap: 10px;
`

const TopTile = styled.div`
  width: 100%;
`

const BottomTiles = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;

  ${up("laptop")} {
    grid-template-columns: 1fr 1fr;
  }
`

const TileLink = styled.a`
  display: block;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
`

const Tile = styled.div<{ ratio: string }>`
  ${({ ratio }) => css`
    position: relative;
    width: 100%;
    aspect-ratio: ${ratio};
    overflow: hidden;
    background: rgba(0, 0, 0, 0.2);
  `}
`

const Poster = styled.img.attrs({
  decoding: "async",
  loading: "lazy",
})<{ isActive: boolean }>`
  ${({ isActive }) => css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${isActive ? 0 : 1};
    transition: opacity 180ms ease;
  `}
`

const Preview = styled(HlsVideo)<{ $isActive: boolean }>`
  ${({ $isActive }) => css`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${$isActive ? 1 : 0};
    transition: opacity 180ms ease;
  `}
`

const Arrow = styled.span`
  font-size: 16px;
`

const EmbeddedVideo = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
`

const PlayIcon = styled.span<{ isVisible: boolean }>`
  ${({ isVisible }) => css`
    position: absolute;
    top: 50%;
    left: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 84px;
    height: 84px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.58);
    color: ${colors.snow};
    transform: translate(-50%, -50%);
    opacity: ${isVisible ? 1 : 0};
    transition: opacity 180ms ease;
    pointer-events: none;
    z-index: 1;

    &::before {
      content: "";
      margin-left: 5px;
      border-top: 13px solid transparent;
      border-bottom: 13px solid transparent;
      border-left: 20px solid currentColor;
    }

    ${up("laptop")} {
      width: 104px;
      height: 104px;

      &::before {
        margin-left: 6px;
        border-top: 16px solid transparent;
        border-bottom: 16px solid transparent;
        border-left: 24px solid currentColor;
      }
    }
  `}
`

const PlayLabel = styled.span<{ isVisible: boolean }>`
  ${({ isVisible }) => css`
    ${bodyBold}
    position: absolute;
    left: 50%;
    bottom: 22px;
    transform: translateX(-50%);
    padding: 10px 14px;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.58);
    color: ${colors.snow};
    font-size: 12px;
    line-height: 1;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
    opacity: ${isVisible ? 1 : 0};
    transition: opacity 180ms ease;
    pointer-events: none;
    z-index: 1;

    ${up("laptop")} {
      bottom: 28px;
      font-size: 13px;
    }
  `}
`

const getYouTubeEmbedUrl = (rawUrl?: string) => {
  if (!rawUrl) return undefined

  try {
    const url = new URL(rawUrl)
    const host = url.hostname.toLowerCase()

    if (host.includes("youtu.be")) {
      const videoId = url.pathname.split("/").filter(Boolean)[0]
      if (videoId) return `https://www.youtube.com/embed/${videoId}`
    }

    if (host.includes("youtube.com") || host.includes("youtube-nocookie.com")) {
      if (url.pathname.includes("/embed/")) {
        return `${url.origin}${url.pathname}`
      }

      if (url.pathname.includes("/shorts/")) {
        const videoId = url.pathname.split("/shorts/")[1]?.split("/")[0]
        if (videoId) return `https://www.youtube.com/embed/${videoId}`
      }

      const videoId = url.searchParams.get("v")
      if (videoId) return `https://www.youtube.com/embed/${videoId}`
    }

    return undefined
  } catch (_error) {
    return undefined
  }
}

const hasPreviewSource = (tile?: VideoTileProps) =>
  Boolean(tile?.streamingUrl || tile?.fallbackUrl || tile?.previewSrc)

const renderVideoTile = (
  tile: VideoTileProps,
  ratio: string,
  index: number,
  isActive: boolean,
  videoRefs: React.MutableRefObject<Array<HTMLVideoElement | null>>,
  onEnter: (index: number) => void,
  onLeave: (index: number) => void
) => {
  const content = (
    <Tile
      ratio={ratio}
      onMouseEnter={() => onEnter(index)}
      onMouseLeave={() => onLeave(index)}
      onFocus={() => onEnter(index)}
      onBlur={() => onLeave(index)}
    >
      {tile.thumbnailSrc && (
        <Poster
          src={tile.thumbnailSrc}
          alt=""
          isActive={hasPreviewSource(tile) && isActive}
        />
      )}
      {hasPreviewSource(tile) && (
        <Preview
          $isActive={isActive}
          active={isActive}
          ref={(node) => {
            videoRefs.current[index] = node
          }}
          muted
          maxPlayCount={tile.playOnce ? 1 : 3}
          playsInline
          preload="none"
          poster={tile.thumbnailSrc}
          streamingUrl={tile.streamingUrl}
          fallbackUrl={tile.fallbackUrl || tile.previewSrc}
          aria-hidden="true"
        />
      )}
    </Tile>
  )

  if (!tile.link) return <TileLink as="div">{content}</TileLink>

  return (
    <TileLink
      href={tile.link}
      target={tile.openInNewTab ? "_blank" : undefined}
      rel={tile.openInNewTab ? "noopener noreferrer" : undefined}
    >
      {content}
    </TileLink>
  )
}

const FeaturedVideo: React.FC<FeaturedVideoProps> = ({
  title,
  sectionButtonText,
  sectionButtonLink,
  sectionButtonTextColor,
  sectionButtonColor,
  backgroundImageSrc,
  backgroundColor,
  primaryVideoThumbnail,
  primaryVideoPreview,
  primaryVideoStreamingUrl,
  primaryVideoFallbackUrl,
  primaryVideoLink,
  leftVideoThumbnail,
  leftVideoPreview,
  leftVideoStreamingUrl,
  leftVideoFallbackUrl,
  leftVideoLink,
  rightVideoThumbnail,
  rightVideoPreview,
  rightVideoStreamingUrl,
  rightVideoFallbackUrl,
  rightVideoLink,
}) => {
  const videoRefs = React.useRef<Array<HTMLVideoElement | null>>([])
  const [activeVideos, setActiveVideos] = React.useState<
    Record<number, boolean>
  >({})
  const [isPrimaryEmbedOpen, setIsPrimaryEmbedOpen] = React.useState(false)
  const [isDocumentVisible, setIsDocumentVisible] = React.useState(true)
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false)
  const [saveDataEnabled, setSaveDataEnabled] = React.useState(false)
  const [hasResolvedPlaybackPreferences, setHasResolvedPlaybackPreferences] =
    React.useState(false)
  const primaryEmbedUrl = React.useMemo(
    () => getYouTubeEmbedUrl(primaryVideoLink),
    [primaryVideoLink]
  )

  const tiles = React.useMemo(
    () => [
      {
        thumbnailSrc: primaryVideoThumbnail,
        previewSrc: primaryVideoPreview,
        streamingUrl: primaryVideoStreamingUrl,
        fallbackUrl: primaryVideoFallbackUrl,
        link: primaryVideoLink,
        openInNewTab: true,
      },
      {
        thumbnailSrc: leftVideoThumbnail,
        previewSrc: leftVideoPreview,
        streamingUrl: leftVideoStreamingUrl,
        fallbackUrl: leftVideoFallbackUrl,
        link: leftVideoLink,
        openInNewTab: true,
      },
      {
        thumbnailSrc: rightVideoThumbnail,
        previewSrc: rightVideoPreview,
        streamingUrl: rightVideoStreamingUrl,
        fallbackUrl: rightVideoFallbackUrl,
        link: rightVideoLink,
        openInNewTab: true,
      },
    ],
    [
      primaryVideoThumbnail,
      primaryVideoPreview,
      primaryVideoStreamingUrl,
      primaryVideoFallbackUrl,
      primaryVideoLink,
      leftVideoThumbnail,
      leftVideoPreview,
      leftVideoStreamingUrl,
      leftVideoFallbackUrl,
      leftVideoLink,
      rightVideoThumbnail,
      rightVideoPreview,
      rightVideoStreamingUrl,
      rightVideoFallbackUrl,
      rightVideoLink,
    ]
  )
  const isPreviewPlaybackAllowed =
    hasResolvedPlaybackPreferences &&
    isDocumentVisible &&
    !prefersReducedMotion &&
    !saveDataEnabled

  const handleEnter = React.useCallback((index: number) => {
    setActiveVideos((current) => ({ ...current, [index]: true }))
  }, [])

  const handleLeave = React.useCallback((index: number) => {
    setActiveVideos((current) => ({ ...current, [index]: false }))
  }, [])

  const handlePrimaryTileClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (!primaryEmbedUrl) return
      event.preventDefault()
      setIsPrimaryEmbedOpen(true)
      setActiveVideos((current) => ({ ...current, 0: false }))
    },
    [primaryEmbedUrl]
  )

  React.useEffect(() => {
    setIsPrimaryEmbedOpen(false)
  }, [primaryVideoLink])

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
    const updateMotionPreference = () => {
      setPrefersReducedMotion(motionQuery.matches)
    }
    const updateDocumentVisibility = () => {
      setIsDocumentVisible(document.visibilityState !== "hidden")
    }
    const connection = (
      navigator as Navigator & { connection?: { saveData?: boolean } }
    ).connection

    updateMotionPreference()
    updateDocumentVisibility()
    setSaveDataEnabled(Boolean(connection?.saveData))
    setHasResolvedPlaybackPreferences(true)
    document.addEventListener("visibilitychange", updateDocumentVisibility)

    if (motionQuery.addEventListener) {
      motionQuery.addEventListener("change", updateMotionPreference)
    } else {
      motionQuery.addListener(updateMotionPreference)
    }

    return () => {
      document.removeEventListener("visibilitychange", updateDocumentVisibility)
      if (motionQuery.removeEventListener) {
        motionQuery.removeEventListener("change", updateMotionPreference)
      } else {
        motionQuery.removeListener(updateMotionPreference)
      }
    }
  }, [])

  React.useEffect(() => {
    if (typeof window === "undefined") return
    if (!isPreviewPlaybackAllowed) return
    if (window.innerWidth >= breakpoints.laptop) return
    if (typeof IntersectionObserver === "undefined") return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const indexValue = (entry.target as HTMLElement).dataset.videoIndex
          if (!indexValue) return
          const index = Number(indexValue)

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            setActiveVideos((current) => ({ ...current, [index]: true }))
            return
          }

          setActiveVideos((current) => ({ ...current, [index]: false }))
        })
      },
      { threshold: [0.5] }
    )

    videoRefs.current.forEach((video, index) => {
      if (!video || !hasPreviewSource(tiles[index])) return
      video.dataset.videoIndex = String(index)
      observer.observe(video)
    })

    return () => {
      observer.disconnect()
    }
  }, [isPreviewPlaybackAllowed, tiles])

  return (
    <Section bgImage={backgroundImageSrc} bgColor={backgroundColor}>
      <Container>
        <Header>
          <Title>{title || "Videos"}</Title>
          {sectionButtonText && sectionButtonLink && (
            <Link href={sectionButtonLink} passHref prefetch={false}>
              <SectionButton
                $buttonColor={sectionButtonColor}
                $buttonTextColor={sectionButtonTextColor}
                target="_blank"
                rel="noopener noreferrer"
              >
                {sectionButtonText}
                <Arrow>&rsaquo;</Arrow>
              </SectionButton>
            </Link>
          )}
        </Header>

        <Grid>
          <TopTile>
            {tiles[0].link ? (
              <TileLink
                href={tiles[0].link}
                onClick={handlePrimaryTileClick}
                onMouseEnter={() => !isPrimaryEmbedOpen && handleEnter(0)}
                onMouseLeave={() => !isPrimaryEmbedOpen && handleLeave(0)}
                onPointerEnter={() => !isPrimaryEmbedOpen && handleEnter(0)}
                onPointerLeave={() => !isPrimaryEmbedOpen && handleLeave(0)}
                onFocus={() => !isPrimaryEmbedOpen && handleEnter(0)}
                onBlur={() => !isPrimaryEmbedOpen && handleLeave(0)}
                target={!primaryEmbedUrl ? "_blank" : undefined}
                rel={!primaryEmbedUrl ? "noopener noreferrer" : undefined}
              >
                <Tile ratio="16 / 9">
                  {isPrimaryEmbedOpen && primaryEmbedUrl ? (
                    <EmbeddedVideo
                      src={`${primaryEmbedUrl}?autoplay=1&rel=0&modestbranding=1`}
                      title={title || "Featured video"}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <>
                      <PlayIcon
                        isVisible={!isPrimaryEmbedOpen}
                        aria-hidden="true"
                      />
                      <PlayLabel
                        isVisible={!isPrimaryEmbedOpen}
                        aria-hidden="true"
                      >
                        Click to view full video
                      </PlayLabel>
                      {tiles[0].thumbnailSrc && (
                        <Poster
                          src={tiles[0].thumbnailSrc}
                          alt=""
                          isActive={
                            hasPreviewSource(tiles[0]) &&
                            Boolean(activeVideos[0]) &&
                            isPreviewPlaybackAllowed
                          }
                        />
                      )}
                      {hasPreviewSource(tiles[0]) && (
                        <Preview
                          $isActive={
                            Boolean(activeVideos[0]) && isPreviewPlaybackAllowed
                          }
                          active={
                            Boolean(activeVideos[0]) && isPreviewPlaybackAllowed
                          }
                          ref={(node) => {
                            videoRefs.current[0] = node
                          }}
                          muted
                          maxPlayCount={3}
                          playsInline
                          preload="none"
                          poster={tiles[0].thumbnailSrc}
                          streamingUrl={tiles[0].streamingUrl}
                          fallbackUrl={
                            tiles[0].fallbackUrl || tiles[0].previewSrc
                          }
                          aria-hidden="true"
                        />
                      )}
                    </>
                  )}
                </Tile>
              </TileLink>
            ) : (
              renderVideoTile(
                tiles[0],
                "16 / 9",
                0,
                Boolean(activeVideos[0]) && isPreviewPlaybackAllowed,
                videoRefs,
                handleEnter,
                handleLeave
              )
            )}
          </TopTile>

          <BottomTiles>
            {renderVideoTile(
              tiles[1],
              "1.45 / 1",
              1,
              Boolean(activeVideos[1]) && isPreviewPlaybackAllowed,
              videoRefs,
              handleEnter,
              handleLeave
            )}
            {renderVideoTile(
              tiles[2],
              "1.45 / 1",
              2,
              Boolean(activeVideos[2]) && isPreviewPlaybackAllowed,
              videoRefs,
              handleEnter,
              handleLeave
            )}
          </BottomTiles>
        </Grid>
      </Container>
    </Section>
  )
}

export default FeaturedVideo
